"use client";

import { useNavigation } from "@app/[locale]/(site)/navContext";
import {
  ChevronDown as ArrowDownIcon,
  ChevronUp as ArrowUpIcon,
  PlusIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export default function Wrapper({
  label,
  open: defaultOpen = false,
  alwaysOpen = false,
  totalSelected,
  children,
  disabled,
  slug,
}: {
  children: ReactNode;
  label: string;
  open?: boolean;
  alwaysOpen?: boolean;
  totalSelected?: number | boolean;
  disabled?: boolean;
  slug?: string;
}) {
  const { flatNavigation } = useNavigation().state;
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!ref.current || disabled) return;

    ref.current.style.height = "auto";
    const height = ref.current.clientHeight;

    ref.current.style.height = "";

    ref.current.style.setProperty("--height", `${height}px`);

    // Timeout is needed to allow the height to be set before the transition
    setTimeout(() => {
      setOpen(!open);
    });

    // Set height to auto after transition
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.style.setProperty("--height", `auto`);
    }, 150);
  };

  const displayedLabel = useMemo(() => {
    if (label !== "Category" || !slug) return label;
    return flatNavigation?.[slug]?.title ?? "Categories";
  }, [slug, label, flatNavigation]);

  if (disabled) {
    return (
      <div className="pointer-events-none overflow-hidden py-2 opacity-50">
        <div className="flex items-center text-left text-base uppercase">
          <span className="grow px-3 py-2">{displayedLabel}</span>

          {alwaysOpen ? null : (
            <button type="button" className="px-2">
              <PlusIcon strokeWidth={1} className="size-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border-gray-150 overflow-hidden">
      <button
        type="button"
        className="flex h-12 w-full items-center text-left text-base font-normal"
        onClick={() => (alwaysOpen ? null : handleClick())}
      >
        <span className="flex grow items-center text-base font-normal">
          {displayedLabel}
          {totalSelected ? (
            <span className="ml-1 bg-black px-1 py-0.5 text-white tabular-nums">
              {totalSelected}
            </span>
          ) : null}
        </span>

        {alwaysOpen ? null : (
          <span className="px-2">
            {open ? (
              <ArrowUpIcon strokeWidth={1} className="size-6" />
            ) : (
              <ArrowDownIcon strokeWidth={1} className="size-6" />
            )}
          </span>
        )}
      </button>
      <div
        ref={ref}
        className={cn(
          "overflow-hidden transition-[height]",
          !alwaysOpen && (open ? "h-[var(--height)]" : "h-0"),
        )}
      >
        {children}
      </div>
    </div>
  );
}
